/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router';
import { BaseLayout } from '@triplabo/web/layouts';
import { isWeb } from '@triplabo/web/utils';
import { isProd } from '@triplabo/web/utils/context';

const Home = lazy(() => import('@triplabo/web/views/Home'));
const DropOff = lazy(() => import('@triplabo/web/views/DropOff'));
const PickUp = lazy(() => import('@triplabo/web/views/PickUp'));
const Profile = lazy(() => import('@triplabo/web/views/Profile'));
const Order = lazy(() => import('@triplabo/web/views/Order'));
const OrderConfirm = lazy(() => import('@triplabo/web/views/OrderConfirm'));
const OrderDetail = lazy(() => import('@triplabo/web/views/OrderDetail'));
const FareEstimate = lazy(() => import('@triplabo/web/views/FareEstimate'));
const PointChange = lazy(() => import('@triplabo/web/views/PointChange'));
const RepayDetail = lazy(() => import('@triplabo/web/views/RepayDetail'));
const Chat = lazy(() => import('@triplabo/web/views/Chat'));
const NotFound = lazy(() => import('@triplabo/web/views/NotFound'));
const EditUserInfo = lazy(() => import('@triplabo/web/views/EditUserInfo'));
const Test = lazy(() => import('@triplabo/web/views/Test'));
const HotelHome = lazy(() => import('@triplabo/web/views/Hotel/Home'));
const HotelDropOff = lazy(() => import('@triplabo/web/views/Hotel/DropOff'));
const Login = lazy(() => import('@triplabo/web/views/Login'));
const SignIn = lazy(() => import('@triplabo/web/views/SignIn'));
const HotelOrderConfirm = lazy(
  () => import('@triplabo/web/views/Hotel/OrderConfirm')
);
const HotelPaymentResult = lazy(
  () => import('@triplabo/web/views/Hotel/PaymentResult')
);
const HotelOrderDetail = lazy(
  () => import('@triplabo/web/views/Hotel/OrderDetail')
);
const Service = lazy(() => import('@triplabo/web/views/Service'));
const Terms = lazy(() => import('@triplabo/web/views/Terms'));
const Privacy = lazy(() => import('@triplabo/web/views/Privacy'));
const ResetPassword = lazy(() => import('@triplabo/web/views/ResetPassword'));
const CouponList = lazy(() => import('@triplabo/web/views/CouponList'));
const ShareCoupon = lazy(() => import('@triplabo/web/views/ShareCoupon'));
const RedeemCoupon = lazy(() => import('@triplabo/web/views/RedeemCoupon'));

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback="Loading...">{view}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path="/" element={<BaseLayout />}>
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={lazyView(<Home />)} />
        <Route path="dropoff" element={lazyView(<DropOff />)} />
        <Route path="pickup" element={lazyView(<PickUp />)} />
        <Route path="profile">
          <Route index element={lazyView(<Profile />)} />
          <Route path="edit" element={lazyView(<EditUserInfo />)} />
        </Route>
        <Route path="orders">
          <Route index element={lazyView(<Order />)} />
          <Route path=":orderId" element={lazyView(<OrderDetail />)} />
          <Route path="confirm" element={lazyView(<OrderConfirm />)} />
        </Route>
        <Route path="change" element={lazyView(<PointChange />)} />
        <Route path="repaydetail" element={lazyView(<RepayDetail />)} />
        <Route path="estimatedetail" element={lazyView(<FareEstimate />)} />
        <Route path="service" element={lazyView(<Service />)} />
        <Route path="terms" element={lazyView(<Terms />)} />
        <Route path="privacy" element={lazyView(<Privacy />)} />
        <Route path="coupons">
          <Route index element={lazyView(<CouponList />)} />
          <Route path="share" element={lazyView(<ShareCoupon />)} />
          <Route path="redeem" element={lazyView(<RedeemCoupon />)} />
        </Route>
        <Route path="chat" element={lazyView(<Chat />)} />
        {!isProd && <Route path="test" element={lazyView(<Test />)} />}
        {isWeb && <Route path="login" element={lazyView(<Login />)} />}
        {isWeb && (
          <Route path="reset-password" element={lazyView(<ResetPassword />)} />
        )}
        <Route path="signin" element={lazyView(<SignIn />)} />
        <Route path="hotel">
          <Route index element={lazyView(<HotelHome />)} />
          <Route path="home" element={lazyView(<HotelHome />)} />
          <Route path="dropoff" element={lazyView(<HotelDropOff />)} />
          <Route path="orders">
            <Route path="confirm" element={lazyView(<HotelOrderConfirm />)} />
            <Route path=":orderId" element={lazyView(<HotelOrderDetail />)} />
          </Route>
          <Route path="payresult" element={lazyView(<HotelPaymentResult />)} />
        </Route>
      </Route>
      <Route path="*" element={lazyView(<NotFound />)} />
    </Route>
  )
);

export default router;
